import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import UserSignupForm from './components/UserSignupForm';
import Button from '../../components/ui/Button';
import AlertBox from '../../components/ui/AlertBox';
import { useAuth } from '../../contexts/authContext';
import { useRedirectParam } from '../../hooks/useRedirectParam';

const KNOWN_FORM_FIELDS = new Set(['first_name', 'last_name', 'email', 'password', 'gender']);

function SignupPage() {
  const navigate = useNavigate();
  useRedirectParam();
  const { register: registerForm, handleSubmit, setError, clearErrors, formState: { errors, isSubmitting } } = useForm();
  const { user, register } = useAuth();
  const [pageError, setPageError] = useState(null);

  useEffect(() => {
    if (user) {
      navigate('/complete-profile/role-selection');
    }
  }, [user, navigate]);

  const onSubmit = async (data) => {
    setPageError(null);
    clearErrors();

    try {
      const response = await register(data);
      console.log('Registration successful:', response);
      toast.success('Registration successful');

      if (user) {
        navigate('/complete-profile/role-selection');
      }
    } catch (error) {
      console.error('Registration failed:', error);

      const getErrorMessage = (val) => {
        if (Array.isArray(val)) return val[0];
        if (typeof val === 'object' && val !== null) {
          return Object.values(val).map(getErrorMessage).join(', ');
        }
        return String(val || '');
      };

      if (typeof error === 'string') {
        setPageError(error);
        toast.error(error);
        return;
      }

      if (error && typeof error === 'object') {
        let generalErrorMessage = null;

        if (error.detail) {
          generalErrorMessage = getErrorMessage(error.detail);
        } else if (error.non_field_errors) {
          generalErrorMessage = getErrorMessage(error.non_field_errors);
        } else if (error.message && typeof error.message === 'string') {
          generalErrorMessage = error.message;
        }

        // Iterate through all error keys and assign field-specific errors
        Object.keys(error).forEach((field) => {
          if (['detail', 'non_field_errors', 'message'].includes(field)) {
            return;
          }

          const message = getErrorMessage(error[field]);

          if (field === 'user' && error.user && typeof error.user === 'object') {
            Object.keys(error.user).forEach((userField) => {
              const userMsg = getErrorMessage(error.user[userField]);
              if (KNOWN_FORM_FIELDS.has(userField)) {
                setError(userField, { type: 'server', message: userMsg });
              } else if (!generalErrorMessage) {
                generalErrorMessage = userMsg;
              }
            });
          } else if (KNOWN_FORM_FIELDS.has(field)) {
            setError(field, { type: 'server', message });
          } else if (!generalErrorMessage) {
            generalErrorMessage = `${field}: ${message}`;
          }
        });

        const finalError = generalErrorMessage || 'Please correct the highlighted errors.';
        setPageError(finalError);
        toast.error(finalError);
      } else {
        const fallback = 'An unexpected error occurred during signup.';
        setPageError(fallback);
        toast.error(fallback);
      }
    }
  };

  return (
    <div className="bg-base-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl text-center font-bold mb-8">Create Your Account</h1>
        <div className="bg-base-100 shadow-xl rounded-box p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            {pageError && (
              <div className="mb-4">
                <AlertBox message={pageError} type="error" />
              </div>
            )}

            <UserSignupForm
              register={registerForm}
              errors={errors}
            />

            <Button
              className="w-full mt-4"
              isLoading={isSubmitting}
            >
              Create Account
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;